import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import fetchToBackend, { type BackendError } from "@/utils/fetchToBackend";
import {
  companyKeys,
  contractingKeys,
  onboardingKeys,
  viewQuotaKeys,
} from "@/lib/query-keys";
import { toastUtil } from "@/utils/toastUtil";
import { capture } from "@/lib/posthog";
import { useUpsell } from "@/providers/UpsellProvider";
import { useRunBidAnalysis } from "@/hooks/mutations/useRunBidAnalysis";
import type { UnlockQualification } from "@/types/domain";

/**
 * Desbloquea una oportunidad consumiendo una vista. Al tener éxito refresca los
 * listados y la cuota. Si no quedan vistas, el backend responde `NO_VIEWS_LEFT`.
 * Desbloquear también la cruza contra los documentos: lo que no se cruzó al
 * momento se explica (esperando bases) o se ofrece (registro cerrado).
 */
export function useUnlockContracting() {
  const qc = useQueryClient();
  const openUpsell = useUpsell();
  const runAnalysis = useRunBidAnalysis();
  return useMutation<
    { qualification: UnlockQualification | null },
    BackendError,
    { contracting_id: number }
  >({
    mutationFn: ({ contracting_id }) =>
      fetchToBackend(`/contractings/${contracting_id}/unlock`, {
        method: "POST",
      }),
    onSuccess: ({ qualification }, { contracting_id }) => {
      capture("contracting_unlocked", { contracting_id });
      // Refrescar listados (para revelar la oportunidad) y el saldo de vistas.
      qc.invalidateQueries({ queryKey: contractingKeys.lists() });
      qc.invalidateQueries({ queryKey: contractingKeys.details() });
      // La vista de empresa también redacta: contratos ganados, catálogo de
      // ítems y grafo de red revelan la nomenclatura al desbloquear.
      qc.invalidateQueries({ queryKey: companyKeys.all });
      // El onboarding de proveedor revela al ganador apenas se desbloquea la obra.
      qc.invalidateQueries({ queryKey: onboardingKeys.all });
      qc.invalidateQueries({ queryKey: viewQuotaKeys.all });
      qc.invalidateQueries({ queryKey: contractingKeys.qualification() });
      // Sin aviso de éxito: el modal se expande con la licitación ya revelada y
      // el saldo de la barra baja. El toast solo repetía lo que ya se ve.
      if (qualification === "WAITING_BASES") {
        toast("Todavía no tenemos sus bases. Te enviaremos el informe por correo cuando las leamos.");
      } else if (qualification === "REGISTRATION_CLOSED") {
        toast("El registro de esta obra ya cerró. ¿Igual quieres analizar sus requisitos?", {
          duration: 15000,
          action: {
            label: "Analizar",
            onClick: () => runAnalysis.mutate({ contracting_id }),
          },
        });
      }
    },
    onError: (err) => {
      // Quedarse sin vistas no es un fallo que se avise de reojo: es el momento
      // de ofrecer el plan, así que va en modal y no en toast.
      if (err?.errorCode === "NO_VIEWS_LEFT") {
        openUpsell("no-views");
        return;
      }
      toastUtil("No se pudo desbloquear la oportunidad.", "error");
    },
  });
}
